<template>
  <div class="cancel-go-component">
    <!-- Top zone: optional accelerator (left) + live cancellation-order summary (right) -->
    <div class="row g-3 mb-4">
      <div class="col-lg-7">
        <AiChatCard
          :companyId="props.companyId"
          :endpoint="`/api/company/${props.companyId}/portfolio/cancel-chat`"
          :title="$t('go_cancel.accelerator_title')"
          :greeting="$t('ai_chat.greetings.cancel')"
          :resolveCorrection="resolveChatCorrection"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>
      <div class="col-lg-5">
        <CancellationOrderSummary
          :mwhToCancel="totalMwhToCancel"
          :statementsCount="statementsCount"
          :beneficiaryTypeLabel="beneficiaryTypeLabel"
          :periodLabel="periodLabel"
          :selectedMwh="totalMwhInTable"
          :status="orderStatus"
        />
      </div>
    </div>

    <CancelStatementsEditor
      ref="editorRef"
      :selectedRegions="selectedRegions"
      :truncatedWarning="truncatedWarning"
      :hasInconsistentSharedFields="hasInconsistentSharedFields"
      :beneficiarySuggestions="beneficiarySuggestions"
      :companyId="props.companyId"
      @shared-defaults:edited="hasInconsistentSharedFields = false"
    />

    <!-- Insufficient Inventory Warning and Buy Missing Button -->
    <!-- Hidden while document criteria are active: volume-only prefill can't honor them; the
         shortfall line under the filter chips and the confirm-gate handle shortfalls instead. -->
    <div
      v-if="isInsufficientInventoryWarningVisible"
      class="d-flex justify-content-between align-items-center gap-3 rounded-3 px-3 py-2 mt-4 bg-danger-subtle text-danger-emphasis border border-danger-subtle"
    >
      <span class="d-flex align-items-center gap-2 small">
        <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
        {{
          $t("go_cancel.insufficient_inventory_warning", {
            availableMwh: totalAvailableMwhInInventory.toFixed(3),
            neededMwh: totalMwhToCancel.toFixed(3),
          })
        }}
      </span>
      <button
        class="btn btn-warning btn-sm ms-3 flex-shrink-0"
        @click="handleBuyMissingGos"
      >
        <FontAwesomeIcon icon="shopping-cart" class="me-1" />
        {{ $t("go_cancel.buy_missing_gos_button") }}
      </button>
    </div>

    <!-- Portfolio selection -->
    <div class="mt-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="font-serif mb-0">
          {{ $t("go_cancel.portfolio_items_title") }}
        </h4>
        <div class="form-check form-switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="autoSelectCertificatesCheckbox"
            v-model="autoSelectCertificates"
          />
          <label class="form-check-label" for="autoSelectCertificatesCheckbox">
            {{ $t("go_cancel.auto_select_certificates_checkbox") }}
          </label>
        </div>
      </div>

      <CarriedSelectionNotice
        v-if="carriedSelection"
        :carried="carriedSelection"
        :canUseAmount="canUseCarriedAmount"
        @use:amount="useCarriedAmount"
        @clear="autoSelectCertificates = true"
      />

      <p class="d-flex align-items-center gap-2 small text-body-tertiary mb-2">
        <FontAwesomeIcon icon="circle-info" class="flex-shrink-0" />
        {{ $t("go_cancel.table_hint") }}
      </p>

      <!-- Document criteria show as the table's filter chips, including on hidden columns. -->
      <PortfolioItemsTable
        ref="portfolioTableRef"
        :selection="selection"
        :enable-count-editing="true"
        :default-visible-columns="TRANSACTION_COLUMNS"
        storage-context="cancel"
        @update:filtered-stats="onFilteredStats"
      >
        <template #below-filters>
          <!-- Always rendered: assistive tech reliably announces changes only in a live region that
               already exists. It carries no spacing classes, so while empty it takes no room. -->
          <div role="status">
            <div
              v-if="isFilterShortfallVisible"
              class="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-2 small bg-danger-subtle text-danger-emphasis border border-danger-subtle"
            >
              <FontAwesomeIcon
                icon="exclamation-triangle"
                class="flex-shrink-0"
              />
              <span>{{ filterShortfallText }}</span>
            </div>
          </div>
        </template>
        <template #footer-action>
          <button
            type="button"
            class="btn btn-success"
            @click="openReviewModal"
            :disabled="!canConfirmOrder || isLoading"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("go_cancel.confirm_order_button") }}
          </button>
        </template>
      </PortfolioItemsTable>
    </div>

    <CancelGoReviewModal
      ref="reviewModalRef"
      :hasRegionMismatch="hasRegionMismatch"
      :hasInsufficientInventory="showInsufficientInventoryWarning"
      :isLoading="isLoading"
      :confirmDisabled="!canConfirmOrder"
      :materializedStatements="materializedStatements"
      :sharedDefaults="editorSharedDefaults"
      :globalOperationNotes="editorGlobalOperationNotes"
      @confirm="handleConfirmOrder"
    />
  </div>
</template>

<script setup lang="ts">
import {
  formatMwh,
  formatNumberWithSpaces,
  formatShortMonthYear,
} from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import CancelGoReviewModal from "@/dashboard/inventory_management/cancel/CancelGoReviewModal.vue";
import CancelStatementsEditor from "@/dashboard/inventory_management/cancel/CancelStatementsEditor.vue";
import {
  cancelBeneficiariesApi,
  type CancelBeneficiarySuggestion,
} from "@/dashboard/inventory_management/cancel/cancelBeneficiariesApi";
import CancellationOrderSummary, {
  type OrderReadinessVariant,
} from "@/dashboard/inventory_management/cancel/CancellationOrderSummary.vue";
import { getCancelAiCorrection } from "@/dashboard/inventory_management/cancel/cancelAiErrors";
import CarriedSelectionNotice, {
  type CarriedSelection,
} from "@/dashboard/inventory_management/cancel/CarriedSelectionNotice.vue";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import type { ChatTurnCorrection } from "@/dashboard/inventory_management/common/aiChatTypes";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import {
  mapAiAnalysisToCancelRows,
  resolveCertificateSelectionFilters,
} from "@/dashboard/inventory_management/common/cancelAiMapping";
import { MAX_CANCEL_STATEMENTS } from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import { EMPTY_FILTER_SUMMARY } from "@/dashboard/inventory_management/common/portfolioFilterChips";
import {
  isItemAvailable,
  sumAvailableMwh,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { TRANSACTION_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type {
  Company,
  FilteredPortfolioStats,
  GoCancelOrderRequest,
} from "@/types";
import { BeneficiaryTypeEnum } from "@/types";
import { CancellationMode } from "@/dashboard/inventory_management/bulk_buy/bulkBuyTypes";
import { debounce } from "lodash-es";
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  type Ref,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  companyId: number;
  company: Company;
}>();

const { t } = useI18n();
const resolveChatCorrection = (correction: ChatTurnCorrection) =>
  getCancelAiCorrection(correction, t);
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({ syncToUrl: true });

const portfolioTableRef =
  useTemplateRef<InstanceType<typeof PortfolioItemsTable>>("portfolioTableRef");
const reviewModalRef = useTemplateRef<{ open: () => void; close: () => void }>(
  "reviewModalRef"
);
const editorRef =
  useTemplateRef<InstanceType<typeof CancelStatementsEditor>>("editorRef");

const autoSelectCertificates = ref<boolean>(true);
// Snapshot of a selection carried over in the URL (Overview ▸ Action ▸ Cancel), shown in a notice
// while auto-select stays paused so it doesn't replace the carried rows. Invariant: a snapshot
// exists only while auto-select is off, so switching auto-select on is all "Clear and auto-select"
// needs — the autoSelect watcher dismisses the notice and re-selects from scratch.
const carriedSelection = ref<CarriedSelection | null>(null);
const isLoading = ref<boolean>(false);
const hasInconsistentSharedFields = ref(false);
const truncatedWarning = ref<string | null>(null);
const beneficiarySuggestions = ref<CancelBeneficiarySuggestion[]>([]);

const fetchBeneficiarySuggestions = async () => {
  try {
    beneficiarySuggestions.value = (
      await cancelBeneficiariesApi.list(props.companyId)
    ).data.beneficiaries;
  } catch (error) {
    // Suggestions are an enhancement with a graceful fallback — BeneficiaryNameCell degrades to a
    // plain text input — so a failure here must not raise the global error toast over a form that
    // still works.
    beneficiarySuggestions.value = [];
    console.warn(
      "[CancelGoComponent] Beneficiary suggestions unavailable:",
      error
    );
  }
};

onMounted(fetchBeneficiarySuggestions);

// What the table's filters leave visible, delivered reactively via `@update:filtered-stats`.
const filteredStats = shallowRef<FilteredPortfolioStats>({
  count: 0,
  totalMwh: 0,
  availableMwh: 0,
  items: [],
});
// The table keeps the same array while the visible rows don't change, so a re-emit with new totals
// alone doesn't re-run auto-select.
const filteredItems = computed(() => filteredStats.value.items);

const selectedPortfolioItems = computed(() => selection.selectedItems);

const totalMwhInTable = computed(() => selection.totalSelectedMwh);

const statementsCount = computed(() => editorRef.value?.cancelRows.length ?? 0);

const totalMwhToCancel = computed(() => editorRef.value?.totalMwhToCancel ?? 0);

const materializedStatements = computed(
  () => editorRef.value?.materializedStatements ?? []
);

const editorSharedDefaults = computed(
  () =>
    editorRef.value?.sharedDefaults ?? {
      beneficiary_type: BeneficiaryTypeEnum.CONSUMER,
      period_start: "",
      period_end: "",
      cancellation_purpose_template: "",
    }
);

const editorGlobalOperationNotes = computed(
  () => editorRef.value?.globalOperationNotes ?? null
);

const cancelRows = computed(() => editorRef.value?.cancelRows ?? []);

const totalAvailableMwhInInventory = computed(() =>
  sumAvailableMwh(portfolioItemsStore.availableItems)
);

const hasSufficientInventory = computed(
  () => totalAvailableMwhInInventory.value >= totalMwhToCancel.value
);

const showInsufficientInventoryWarning = computed(
  () => totalMwhToCancel.value > 0 && !hasSufficientInventory.value
);

// The table's view of its filters: whether any is active, and which still hold what the document
// applied (their chips carry the "From document" / "No matching inventory" tags).
const tableFilterSummary = computed(
  () => portfolioTableRef.value?.filterSummary ?? EMPTY_FILTER_SUMMARY
);

const hasActiveDocumentFilters = computed(
  () => tableFilterSummary.value.documentColumns.length > 0
);

const isInsufficientInventoryWarningVisible = computed(
  () =>
    showInsufficientInventoryWarning.value && !hasActiveDocumentFilters.value
);

const hasFilterShortfall = computed(
  () =>
    totalMwhToCancel.value > 0 &&
    tableFilterSummary.value.hasActiveFilters &&
    filteredStats.value.availableMwh < totalMwhToCancel.value
);

const isFilterShortfallVisible = computed(
  () => hasFilterShortfall.value && !isInsufficientInventoryWarningVisible.value
);

const filterShortfallText = computed(() => {
  const needed = formatNumberWithSpaces(totalMwhToCancel.value);
  return tableFilterSummary.value.unavailableColumns.length > 0
    ? t("go_cancel.filter_shortfall_unavailable", { needed })
    : t("go_cancel.filter_shortfall", {
        available: formatNumberWithSpaces(filteredStats.value.availableMwh),
        needed,
      });
});

// --- Cancellation-order summary readout (right-hand panel) ---
const beneficiaryTypeLabel = computed(() =>
  t(
    `go_cancel.form.beneficiary_type.${editorSharedDefaults.value.beneficiary_type}`
  )
);

const periodLabel = computed(() => {
  const { period_start, period_end } = editorSharedDefaults.value;
  if (!period_start || !period_end) return t("go_cancel.no_data_short");
  return `${formatShortMonthYear(period_start)} – ${formatShortMonthYear(period_end)}`;
});

// Mirrors the canConfirmOrder gate, but surfaces the *first* unmet step so the summary
// reads as an intentional next-action guide rather than a binary ready/not-ready flag.
const orderStatus = computed<{ label: string; variant: OrderReadinessVariant }>(
  () => {
    if (!(editorRef.value?.isValid ?? false)) {
      return {
        label: t("go_cancel.summary_status_incomplete"),
        variant: "pending",
      };
    }
    // Selecting can't fix these two, so they come before the selection steps.
    if (!hasSufficientInventory.value) {
      return {
        label: t("go_cancel.summary_status_insufficient"),
        variant: "warning",
      };
    }
    if (hasFilterShortfall.value) {
      return {
        label: t("go_cancel.summary_status_filter_shortfall"),
        variant: "warning",
      };
    }
    if (selectedPortfolioItems.value.length === 0) {
      return {
        label: t("go_cancel.summary_status_select"),
        variant: "pending",
      };
    }
    if (totalMwhInTable.value !== totalMwhToCancel.value) {
      return {
        label: t("go_cancel.summary_status_match", {
          mwh: formatMwh(totalMwhToCancel.value),
        }),
        variant: "pending",
      };
    }
    return { label: t("go_cancel.summary_ready"), variant: "ready" };
  }
);

const onFilteredStats = (stats: FilteredPortfolioStats) => {
  filteredStats.value = stats;
};

const selectedRegions = computed(() => {
  const regions = new Set<string>();
  selectedPortfolioItems.value.forEach((item) => {
    if (item.registry_region) {
      regions.add(item.registry_region);
    }
  });
  return Array.from(regions);
});

const hasRegionMismatch = computed(
  () => editorRef.value?.hasRegionMismatch ?? false
);

// when any statement targets a registry that cannot cancel directly, process
// the order via the compliant-agent flow; otherwise send no mode.
const derivedCancellationMode = computed<CancellationMode | null>(() =>
  editorRef.value?.hasUnsupportedRegistry
    ? CancellationMode.COMPLIANT_AGENT
    : null
);

// Derived from orderStatus so the readiness gate lives in one place: orderStatus
// reaches the 'ready' variant exactly when all confirm-conditions hold (valid
// statements, sufficient inventory, enough matching the filters, a non-empty
// selection, and matching MWh).
// isLoading is AND'd in only here so the summary readout doesn't flip mid-submit.
const canConfirmOrder = computed(
  () => orderStatus.value.variant === "ready" && !isLoading.value
);

const handleFileAnalysisComplete = async (data: any) => {
  hasInconsistentSharedFields.value = false;
  truncatedWarning.value = null;
  // Drop the prior document's filters first, so an upload reset (data === null) also clears them.
  // The table clears only those still holding the document's values: a filter the user edited is
  // theirs and survives.
  portfolioTableRef.value?.setDocumentFilters(null);
  if (!data || !editorRef.value) {
    return;
  }

  try {
    const aiResult = mapAiAnalysisToCancelRows(
      data,
      editorRef.value.sharedDefaults
    );
    const truncated = aiResult.rows.length > MAX_CANCEL_STATEMENTS;
    const rowsToLoad = truncated
      ? aiResult.rows.slice(0, MAX_CANCEL_STATEMENTS)
      : aiResult.rows;
    const rowsWithTouched = rowsToLoad.map((row) => ({
      temp_id: row.temp_id,
      beneficiary_name: row.beneficiary_name,
      registry_country_region: row.registry_country_region ?? "",
      registry_code: row.registry_code ?? null,
      address: row.address ?? null,
      mwh_cancelled: row.mwh_cancelled,
      keep_statement_private: row.keep_statement_private,
      touched: true,
    }));
    editorRef.value.setRows(
      rowsWithTouched,
      aiResult.sharedDefaults,
      aiResult.globalOperationNotes ?? ""
    );
    if (aiResult.hasInconsistentSharedFields || rowsWithTouched.length === 0) {
      editorRef.value.touchAll();
    }

    if (rowsWithTouched.length === 0) {
      editorRef.value.addCancelStatement();
    }

    if (truncated) {
      truncatedWarning.value = t("go_cancel.analysis_truncated_warning", {
        total: aiResult.rows.length,
        max: MAX_CANCEL_STATEMENTS,
      });
    }

    hasInconsistentSharedFields.value = aiResult.hasInconsistentSharedFields;

    if (aiResult.certificateSelection && portfolioTableRef.value) {
      const resolved = resolveCertificateSelectionFilters(
        aiResult.certificateSelection,
        portfolioTableRef.value.getFilterOptions()
      );
      // The document's criteria become ordinary table filters, their chips tagged "From document".
      if (Object.keys(resolved.filters).length > 0) {
        portfolioTableRef.value.setDocumentFilters(resolved);
      }
    }

    // No success toast here: the chat assistant's reply already narrates the outcome of each turn.
  } catch (error) {
    globalErrorHandler(error);
  }
};

const openReviewModal = () => {
  editorRef.value?.touchAll();
  if (canConfirmOrder.value) {
    reviewModalRef.value?.open();
  }
};

// Everything a submitted order leaves behind. New page state belongs here, so none of it outlives the
// order it described.
const resetOrderForm = () => {
  selection.clearSelection();
  editorRef.value?.reset();
  portfolioTableRef.value?.setDocumentFilters(null);
  hasInconsistentSharedFields.value = false;
  truncatedWarning.value = null;
  autoSelectCertificates.value = false;
  carriedSelection.value = null;
};

const handleConfirmOrder = async () => {
  if (!canConfirmOrder.value || isLoading.value || !editorRef.value) return;
  isLoading.value = true;

  const requestPayload: GoCancelOrderRequest = {
    statements: editorRef.value.materializedStatements,
    portfolio_items: selection.selectedItemsForApi,
    global_operation_notes: editorRef.value.globalOperationNotes,
    has_region_mismatch: editorRef.value.hasRegionMismatch,
    cancellation_mode: derivedCancellationMode.value,
  };

  try {
    await goSolidApi.post(
      `/api/company/${props.companyId}/portfolio/cancel`,
      requestPayload
    );
    globalSuccessHandler(
      t("common.success"),
      t("go_cancel.order_submitted_successfully")
    );

    resetOrderForm();
    // The order claimed the selected items and split partial ones, so the cached rows are stale:
    // re-picking one fails on the backend. Awaited before isLoading drops, so nothing can be
    // confirmed against the old rows.
    const refreshes = await Promise.allSettled([
      portfolioItemsStore.refetchItems(),
      fetchBeneficiarySuggestions(),
    ]);
    refreshes
      .filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      )
      .forEach((result) => globalErrorHandler(result.reason));
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const navigateToInventoryTab = inject("navigateToInventoryTab") as
  ((tabId: string) => void) | undefined;
const prefillBuyVolume = inject<Ref<number | null>>(
  "prefillBuyVolume",
  ref(null)
);

const handleBuyMissingGos = () => {
  const shortfall = totalMwhToCancel.value - totalAvailableMwhInInventory.value;
  if (shortfall <= 0) return;

  prefillBuyVolume.value = shortfall;
  navigateToInventoryTab?.("buy");
};

// Selects oldest-vintage-first from the reactively filtered set, so document criteria (applied as
// table filters) are honored automatically and never relaxed to non-matching certificates.
const selectCertificatesForMwh = (mwhNeeded: number) => {
  selection.clearSelection();

  if (mwhNeeded <= 0) return;

  const availableItems = filteredItems.value.filter(isItemAvailable);

  const sortedItems = [...availableItems].sort((a, b) => {
    const monthA = a.production_month ?? "9999-99";
    const monthB = b.production_month ?? "9999-99";
    if (monthA !== monthB) {
      return monthA.localeCompare(monthB);
    }
    return a.count - b.count;
  });

  let remaining = mwhNeeded;
  for (const item of sortedItems) {
    if (remaining <= 0) break;
    const selectCount = Math.min(item.count, remaining);
    selection.updateItemSelectedCount(item.id, selectCount);
    remaining -= selectCount;
  }
};

const runAutoSelectCertificates = () => {
  if (!autoSelectCertificates.value) return;
  selectCertificatesForMwh(totalMwhToCancel.value);
};

const debouncedAutoSelect = debounce(runAutoSelectCertificates, 500);

// Toggling on re-selects immediately (replacing any carried rows); toggling off leaves the current
// selection for manual editing.
watch(autoSelectCertificates, (enabled) => {
  debouncedAutoSelect.cancel();
  if (enabled) {
    carriedSelection.value = null;
    runAutoSelectCertificates();
  }
});

// Immediate so a selection restored during setup pauses auto-select before its first debounced run
// (which, with no statement MWh yet, would clear it); later restores happen when inventory loads,
// still before the table first reports its rows.
watch(
  () => selection.restoredFromUrl,
  (restored) => {
    if (!restored) return;
    autoSelectCertificates.value = false;
    carriedSelection.value = {
      rows: selectedPortfolioItems.value.length,
      mwh: selection.totalSelectedMwh,
      partial: selection.partialItemCount,
    };
  },
  { immediate: true }
);

// Offered only while no statement has an MWh value, so it never overwrites what the operator typed.
const canUseCarriedAmount = computed(() =>
  cancelRows.value.every((row) => !row.mwh_cancelled)
);

const useCarriedAmount = (mwh: number) => {
  editorRef.value?.setStatementMwh(0, mwh);
  carriedSelection.value = null;
};

// One effect drives auto-select reactively: MWh typing and filter changes (incl. document criteria)
// both re-run it (debounced) from the freshly filtered set.
watch([filteredItems, totalMwhToCancel], () => {
  if (autoSelectCertificates.value) debouncedAutoSelect();
});

onUnmounted(() => {
  debouncedAutoSelect.cancel();
  selection.clearSelection();
});
</script>
